/**
 * Native ブリッジ
 *
 * Hokage 等のネイティブアプリ側と特定のメッセージタイプでやり取りするための薄いラッパー。
 * 各メソッドは `sendMessageToWebView` を使ってリクエスト/レスポンスし、
 * Native 側のハンドラが応答しなかった場合（タイムアウト・WebView 非環境）でも
 * 安全なデフォルト値を返す。
 *
 * Hokage 側で対応しているメッセージタイプ:
 *   - `REQUEST_DEVICE_INFO` / `REQUEST_AUTH_INFO` / `DEVICE_CHECK` 等
 *   参考: `Hokage/ios/Hokage/Views/WebViewScreen.swift`
 */
import {
  isWebView,
  postMessageToNative,
  sendMessageToWebView,
} from "./webview";

/** iOS UIFeedbackGenerator に対応する触覚スタイル */
const HAPTIC_STYLE = {
  /** 軽いインパクト — サムネイルタップ、チェックボックス切替 */
  IMPACT_LIGHT: "impact_light",
  /** 中程度のインパクト — プレゼント送信、メニュー操作 */
  IMPACT_MEDIUM: "impact_medium",
  /** 重いインパクト */
  IMPACT_HEAVY: "impact_heavy",
  /** 柔らかいインパクト */
  IMPACT_SOFT: "impact_soft",
  /** 硬いインパクト */
  IMPACT_RIGID: "impact_rigid",
  /** 成功通知 — いいね、ブックマーク追加 */
  NOTIFICATION_SUCCESS: "notification_success",
  /** 警告通知 */
  NOTIFICATION_WARNING: "notification_warning",
  /** エラー通知 — 残高不足、ポイント不足 */
  NOTIFICATION_ERROR: "notification_error",
  /** セレクション — ナビゲーションタブ切替、スワイプしきい値到達 */
  SELECTION: "selection",
} as const;

export type HapticStyle = (typeof HAPTIC_STYLE)[keyof typeof HAPTIC_STYLE];

export { HAPTIC_STYLE };

export type PermissionTarget = "camera" | "microphone" | "notification";
export type PermissionStatus = "granted" | "denied" | "undetermined";

type PermissionResult = {
  target: PermissionTarget;
  status: PermissionStatus;
};

const PERMISSION_TIMEOUT_MS = 5000;
const RESPONSE_TIMEOUT_MS = 3000;

/**
 * Native にメッセージを送り、2系統のどちらか先に来たレスポンスで解決する。
 *
 * Native アプリによって応答チャネルが異なるため、両方を同時に待ち受ける:
 * - 系統A: `window.handleNativeEvent(data)` コールバック（Hokage 等）
 * - 系統B: `window.dispatchEvent(new CustomEvent(eventName, { detail }))`（KaguraAuth 等）
 *
 * どちらか早い方で解決し、タイムアウト時は `null` を返す（reject しない）。
 * 注意: `handleNativeEvent` を共有するため、`sendMessageToWebView` と同様に
 * 並行呼び出しは未サポート（一度に 1 件のみ）。
 */
function sendAndAwaitResponse<T>(
  message: { type: string },
  eventName: string,
  timeoutMs = RESPONSE_TIMEOUT_MS,
): Promise<T | null> {
  return new Promise((resolve) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const eventHandler = (e: Event) => settle((e as CustomEvent).detail as T);
    const callbackHandler = (data: unknown) => settle(data as T);

    const settle = (value: T | null) => {
      if (settled) return;
      settled = true;
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      window.removeEventListener(eventName, eventHandler);
      if (window.handleNativeEvent === callbackHandler) {
        delete window.handleNativeEvent;
      }
      resolve(value);
    };

    // WebView ブリッジが無い（通常ブラウザ等）→ レスポンスは来ないので即 null
    if (!isWebView()) {
      resolve(null);
      return;
    }

    window.addEventListener(eventName, eventHandler);
    window.handleNativeEvent = callbackHandler;

    timeoutId = setTimeout(() => settle(null), timeoutMs);

    postMessageToNative(message);
  });
}

/**
 * Native にメッセージを送り、CustomEvent でレスポンスを受け取る。
 * Hokage は `dispatchEventToWeb` で `window.dispatchEvent(new CustomEvent(...))` を使って返す。
 */
function sendAndListenEvent<T>(
  message: { type: string; payload?: unknown },
  eventName: string,
  timeoutMs = PERMISSION_TIMEOUT_MS,
): Promise<T | null> {
  return new Promise((resolve) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const handler = (e: Event) => {
      if (settled) return;
      settled = true;
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      window.removeEventListener(eventName, handler);
      resolve((e as CustomEvent).detail as T);
    };

    window.addEventListener(eventName, handler);

    timeoutId = setTimeout(() => {
      if (settled) return;
      settled = true;
      window.removeEventListener(eventName, handler);
      resolve(null);
    }, timeoutMs);

    postMessageToNative(message);
  });
}

export type LoginIdentifiers = {
  lang: string;
  idfv?: string;
  gps_adid?: string;
  android_original_id?: string;
  appVersion?: string;
};

export type DeviceIdentifiers = {
  lang: string;
  idfv?: string;
  gps_adid?: string;
};

export type DeviceCheckResult =
  | {
      platform: "ios";
      device_token: string;
      idfa: string;
      idfv: string;
    }
  | {
      platform: "android";
      android_original_id: string;
      gps_adid: string;
    };

export type AuthInfo = {
  email: string;
  pwd: string;
  userId?: string;
  token?: string;
  applicationId?: string;
  googleAccountId?: string;
  appleUserId?: string;
  lineId?: string;
  scFlag?: boolean;
};

const asString = (value: unknown): string | undefined => {
  return typeof value === "string" && value.length > 0 ? value : undefined;
};

export type UpdateDeviceTokenResult = {
  success: boolean;
  platform: string;
  deviceToken?: string;
  error?: string;
};

export type ProductPrice = {
  productId: string;
  price: number;
  currency: string;
  displayPrice: string;
};

export type ProductPricesResult = {
  products: ProductPrice[];
};

export type PurchaseFailureCode = "notInApp" | "generic";

export type PurchaseEvent =
  | { status: "succeeded"; productId: string; price?: number }
  | { status: "cancelled" }
  | { status: "failed"; code: PurchaseFailureCode; message?: string };

export const native = {
  /**
   * Native から `lang` / `idfv` / `gps_adid` / `android_original_id` / `appVersion` を取得する。
   * ログイン・登録時のリクエストパラメータとして使用。
   */
  async getLoginIdentifiers(): Promise<LoginIdentifiers> {
    const res = await sendMessageToWebView<Record<string, unknown>>({
      type: "REQUEST_DEVICE_INFO",
    });
    return {
      lang: asString(res?.lang) ?? "",
      idfv: asString(res?.idfv),
      gps_adid: asString(res?.gps_adid),
      android_original_id: asString(res?.android_original_id),
      appVersion: asString(res?.appVersion),
    };
  },

  /**
   * `getLoginIdentifiers` の最低限版。`lang` と端末識別子のみが必要な場面で使う。
   */
  async getDeviceIdentifiers(): Promise<DeviceIdentifiers> {
    const res = await sendMessageToWebView<Record<string, unknown>>({
      type: "REQUEST_DEVICE_INFO",
    });
    return {
      lang: asString(res?.lang) ?? "",
      idfv: asString(res?.idfv),
      gps_adid: asString(res?.gps_adid),
    };
  },

  /**
   * リセマラチェック用デバイス識別子を取得する。
   * Hokage は `DEVICE_CHECK_RESULT` ではなく `handleNativeEvent` 経由でフラットに返してくるため、
   * platform を `idfa`/`android_original_id` の有無で推測する。
   */
  async getDeviceCheck(): Promise<{ result: DeviceCheckResult | null }> {
    const res = await sendMessageToWebView<Record<string, unknown>>({
      type: "DEVICE_CHECK",
    });
    if (!res) return { result: null };

    const deviceToken = asString(res.deviceToken);
    const idfa = asString(res.idfa);
    const idfv = asString(res.idfv);
    const androidOriginalId = asString(res.android_original_id);
    const gpsAdid = asString(res.gps_adid);

    if (androidOriginalId || gpsAdid) {
      return {
        result: {
          platform: "android",
          android_original_id: androidOriginalId ?? "",
          gps_adid: gpsAdid ?? "",
        },
      };
    }
    if (deviceToken || idfa || idfv) {
      return {
        result: {
          platform: "ios",
          device_token: deviceToken ?? "",
          idfa: idfa ?? "",
          idfv: idfv ?? "",
        },
      };
    }
    return { result: null };
  },

  /**
   * Native の Keychain 等に保存された認証情報を取得する。
   * 未保存・WebView 非環境では `null` を返す。
   *
   * Native アプリによって応答チャネルが異なる:
   * - Hokage 等: `handleNativeEvent` コールバック
   * - KaguraAuth 等: `authInfoResult` の CustomEvent
   * どちらでも受け取れるよう `sendAndAwaitResponse` で両方を待つ。
   */
  async requestAuthInfo(): Promise<AuthInfo | null> {
    const res = await sendAndAwaitResponse<Record<string, unknown> | null>(
      { type: "REQUEST_AUTH_INFO" },
      "authInfoResult",
    );
    const email = asString(res?.email);
    const pwd = asString(res?.pwd);
    if (!email || !pwd) return null;
    return {
      email,
      pwd,
      userId: asString(res?.userId),
      token: asString(res?.token),
      applicationId: asString(res?.applicationId),
      googleAccountId: asString(res?.googleAccountId),
      appleUserId: asString(res?.appleUserId),
      lineId: asString(res?.lineId),
      scFlag: typeof res?.scFlag === "boolean" ? res.scFlag : undefined,
    };
  },

  /**
   * ネイティブ決済を起動する。
   * Hokage/CoachMe (iOS) は URL インターセプト方式で `xxx_point_package_N` のURLを
   * `decidePolicyFor` でキャッチして課金フローを開始する。
   * fire-and-forget。完了は `purchaseEvent` カスタムイベントで通知される。
   */
  startPurchase(packageId: string): void {
    window.location.href = `/${packageId}`;
  },

  /**
   * 購入完了/キャンセル/失敗を購読する。返り値の関数で unsubscribe。
   */
  onPurchaseEvent(handler: (event: PurchaseEvent) => void): () => void {
    const listener = (e: Event) => {
      handler((e as CustomEvent<PurchaseEvent>).detail);
    };
    window.addEventListener("purchaseEvent", listener);
    return () => window.removeEventListener("purchaseEvent", listener);
  },

  /**
   * Native 側にパーミッションの状態を問い合わせる。
   * Hokage は CustomEvent `checkPermissionResult` でレスポンスを返す。
   */
  async checkPermission(
    target: PermissionTarget,
  ): Promise<PermissionResult | null> {
    return sendAndListenEvent<PermissionResult>(
      { type: "CHECK_PERMISSION", payload: { target } },
      "checkPermissionResult",
    );
  },

  /**
   * Native 側にパーミッションのリクエスト（OS プロンプト表示）を依頼する。
   * Hokage は CustomEvent `requestPermissionResult` でレスポンスを返す。
   */
  async requestPermission(
    target: PermissionTarget,
  ): Promise<PermissionResult | null> {
    return sendAndListenEvent<PermissionResult>(
      { type: "REQUEST_PERMISSION", payload: { target } },
      "requestPermissionResult",
    );
  },

  /**
   * Native の設定画面を開く（パーミッション拒否時に案内）。
   */
  openAppSettings(): void {
    postMessageToNative({ type: "OPEN_APP_SETTINGS" });
  },

  /**
   * Native に触覚フィードバックを要求する（fire-and-forget）。
   * WebView 環境でなければ何もしない。
   */
  triggerHapticFeedback(style: HapticStyle = HAPTIC_STYLE.IMPACT_MEDIUM): void {
    if (!isWebView()) return;
    postMessageToNative({ type: "HAPTIC_FEEDBACK", payload: { style } });
  },

  /**
   * Native の Keychain・SecureStorage を全消去する（fire-and-forget）。
   * Hokage 側で DELETE_KEYCHAIN メッセージを受け取りストレージを削除する。
   */
  deleteKeychain(): void {
    postMessageToNative({ type: "DELETE_KEYCHAIN" });
  },

  /**
   * ログアウトを Native に通知する（fire-and-forget）。
   * Hokage 側で LOGOUT メッセージを受け取り認証情報を破棄する。
   * WebView 非環境では no-op。
   */
  logout(): void {
    postMessageToNative({ type: "LOGOUT" });
  },

  /**
   * ログイン成功を Native に通知する（fire-and-forget）。
   * Hokage 側で Keychain に credential を保存し、次回起動時の自動ログインに使用する。
   * WebView 環境でなければ何もしない。
   */
  notifyLoginSuccess(payload: {
    email: string;
    pwd: string;
    userId: string;
    token: string;
    scFlag?: boolean;
  }): void {
    if (!isWebView()) return;
    postMessageToNative({ type: "LOGIN_SUCCESS", payload });
  },

  /**
   * 登録成功を Native に通知する（fire-and-forget）。
   * Hokage 側で Keychain に credential を保存し、次回起動時の自動ログインに使用する。
   * WebView 環境でなければ何もしない。
   */
  notifyRegisterSuccess(payload: {
    email: string;
    pwd: string;
    userId: string;
    token: string;
    scFlag?: boolean;
  }): void {
    if (!isWebView()) return;
    postMessageToNative({ type: "REGISTER_SUCCESS", payload });
  },

  /**
   * 着信を Native に通知してバイブレーションを開始させる（fire-and-forget）。
   * WebView 環境でなければ何もしない。
   */
  notifyIncomingCallVibrate(): void {
    if (!isWebView()) return;
    postMessageToNative({ type: "INCOMING_CALL_VIBRATE" });
  },

  /**
   * 着信リング終了を Native に通知してバイブレーションを停止させる（fire-and-forget）。
   * 拒否時・通話開始時に送信する。
   * WebView 環境でなければ何もしない。
   */
  notifyCallEnded(): void {
    if (!isWebView()) return;
    postMessageToNative({ type: "CALL_ENDED" });
  },

  /**
   * スワイプバック（WKWebView の戻るジェスチャー）を無効化する（fire-and-forget）。
   * 通話中・ライブ視聴中など、誤操作で画面を離脱させたくない場面でマウント時に送信する。
   * WebView 環境でなければ何もしない。
   */
  disableSwipeBack(): void {
    if (!isWebView()) return;
    postMessageToNative({
      type: "SET_SWIPE_BACK_ENABLED",
      payload: { enabled: false },
    });
  },

  /**
   * スワイプバックを再有効化する（fire-and-forget）。
   * disableSwipeBack() を呼んだ画面のアンマウント時に送信し、元の状態に戻す。
   * WebView 環境でなければ何もしない。
   */
  enableSwipeBack(): void {
    if (!isWebView()) return;
    postMessageToNative({
      type: "SET_SWIPE_BACK_ENABLED",
      payload: { enabled: true },
    });
  },

  /**
   * ストアのアプリレビュー（SKStoreReviewController / In-App Review）を起動するよう
   * Native に依頼する（fire-and-forget）。レビュー訴求 LP のボタンから呼ぶ。
   * ※ Native 側（Carina）で REQUEST_APP_REVIEW ハンドラの実装が必要。
   * WebView 環境でなければ何もしない。
   */
  requestAppReview(): void {
    if (!isWebView()) return;
    postMessageToNative({ type: "REQUEST_APP_REVIEW" });
  },

  /**
   * Native に登録済みのデバイストークンをサーバに再送信させる。
   * `updateDeviceTokenResult` CustomEvent でレスポンスを受け取る。
   */
  async updateDeviceToken(): Promise<UpdateDeviceTokenResult | null> {
    return sendAndListenEvent<UpdateDeviceTokenResult>(
      { type: "UPDATE_DEVICE_TOKEN" },
      "updateDeviceTokenResult",
    );
  },

  /**
   * Native から StoreKit の商品一覧（価格・通貨・表示価格）を取得する。
   * `productPricesResult` CustomEvent でレスポンスを受け取る。
   */
  async getProductPrices(): Promise<ProductPricesResult | null> {
    return sendAndListenEvent<ProductPricesResult>(
      { type: "REQUEST_PRODUCT_PRICES" },
      "productPricesResult",
    );
  },
};
